import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  DatePicker,
  Flex,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  message,
} from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  NumberOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import { Timestamp } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import {
  notificationTimeout,
  notificationType,
  scrollHeight,
} from '../common/constants';
import notValid from '../common/notValid';
import pluralise from '../common/pluralise';
import {
  firListDoorKeypadPins,
  firAddDoorKeypadPin,
  firUpdateDoorKeypadPin,
  firDeleteDoorKeypadPin,
  firGetDoorChannels,
} from '../firebase';

/**
 * Door Keypad PINs — Firestore-managed PIN editor (rev 120).
 *
 * Adds a new PIN row that ATACT staff can create + share with someone
 * (cleaner, contractor, temp), optionally with an expiry timestamp so
 * the access self-revokes. Removing the row revokes immediately
 * (Pi-side filter checks expiresAt + presence at swipe time).
 *
 * Tab gating: SuperAdmin + root (same as Door Staff Cards).
 *
 * The static `keypadPin` value in each Pi's config.json remains as a
 * non-expiring master fallback — useful if Firestore is unreachable
 * or for "always works" admin access.
 */
export default function SiteConfigDoorKeypadPins({ siteId }) {
  const [pins, setPins] = useState([]);
  const [doors, setDoors] = useState({});  // channel code -> { isVisitorEntry, label }
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingPin, setEditingPin] = useState(null);
  const [hideExpired, setHideExpired] = useState(true);

  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();

  const { timeoutError, timeoutSuccess } = notificationTimeout;
  const { typeError, typeSuccess } = notificationType;

  const openMessage = useCallback(
    (content, duration, type) => {
      messageApi.open({ content, duration, type });
    },
    [messageApi],
  );

  const fetchAll = useCallback(
    async (id) => {
      if (notValid(id)) return;
      setLoading(true);
      try {
        const [pinList, doorChannels] = await Promise.all([
          firListDoorKeypadPins(id),
          firGetDoorChannels(id),
        ]);
        setPins(pinList);
        setDoors(doorChannels || {});
      } catch (error) {
        console.error('-- Error fetching keypad PINs:', error);
        openMessage('Error fetching keypad PINs', timeoutError, typeError);
      } finally {
        setLoading(false);
      }
    },
    [openMessage, timeoutError, typeError],
  );

  useEffect(() => {
    fetchAll(siteId);
  }, [siteId, fetchAll]);

  const doorOptions = useMemo(() => {
    const opts = [{ value: null, label: 'All doors at this site' }];
    Object.entries(doors).forEach(([code, meta]) => {
      opts.push({ value: code, label: `${code} — ${meta?.label || ''}` });
    });
    return opts;
  }, [doors]);

  const handleAdd = () => {
    setEditingPin(null);
    form.resetFields();
    form.setFieldsValue({ doorId: null });
    setModalOpen(true);
  };

  const handleEdit = (record) => {
    setEditingPin(record);
    form.setFieldsValue({
      pin: record.pin,
      label: record.label,
      doorId: record.doorId || null,
      expiresAt: record.expiresAt?.toDate ? dayjs(record.expiresAt.toDate()) : null,
    });
    setModalOpen(true);
  };

  const handleDelete = async (pinId) => {
    try {
      await firDeleteDoorKeypadPin(siteId, pinId);
      openMessage('PIN removed', timeoutSuccess, typeSuccess);
      fetchAll(siteId);
    } catch (error) {
      console.error('-- Error deleting PIN:', error);
      openMessage('Error removing PIN', timeoutError, typeError);
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const uid = getAuth().currentUser?.uid || '';
      const payload = {
        pin: String(values.pin).trim(),
        label: String(values.label).trim(),
        doorId: values.doorId || null,
        expiresAt: values.expiresAt ? Timestamp.fromDate(values.expiresAt.toDate()) : null,
      };

      if (editingPin) {
        await firUpdateDoorKeypadPin(siteId, editingPin.id, payload);
        openMessage('PIN updated', timeoutSuccess, typeSuccess);
      } else {
        await firAddDoorKeypadPin(siteId, payload, uid);
        openMessage('PIN added', timeoutSuccess, typeSuccess);
      }
      setModalOpen(false);
      fetchAll(siteId);
    } catch (error) {
      if (error?.errorFields) return;
      console.error('-- Error saving PIN:', error);
      openMessage('Error saving PIN', timeoutError, typeError);
    }
  };

  const rowsVisible = useMemo(() => {
    if (!hideExpired) return pins;
    const now = Date.now();
    return pins.filter((p) => {
      const ms = p.expiresAt?.toMillis ? p.expiresAt.toMillis() : null;
      return ms === null || ms > now;
    });
  }, [pins, hideExpired]);

  const columns = [
    {
      title: 'PIN',
      dataIndex: 'pin',
      key: 'pin',
      width: 130,
      render: (v) => <code style={{ fontSize: 16 }}>{v}</code>,
    },
    {
      title: 'Label',
      dataIndex: 'label',
      key: 'label',
      sorter: (a, b) => (a.label || '').localeCompare(b.label || ''),
    },
    {
      title: 'Scope',
      dataIndex: 'doorId',
      key: 'doorId',
      render: (v) => (v ? `Door ${v}` : <em style={{ color: '#888' }}>all doors</em>),
    },
    {
      title: 'Expires',
      dataIndex: 'expiresAt',
      key: 'expiresAt',
      render: (v) => {
        if (!v?.toDate) return <em style={{ color: '#888' }}>never</em>;
        const d = dayjs(v.toDate());
        const expired = d.isBefore(dayjs());
        return (
          <Tooltip title={d.format('YYYY-MM-DD HH:mm:ss')}>
            <Tag color={expired ? 'red' : 'blue'} icon={<ClockCircleOutlined />}>
              {expired ? `expired ${d.fromNow ? d.fromNow() : d.format('YYYY-MM-DD')}` : d.format('YYYY-MM-DD HH:mm')}
            </Tag>
          </Tooltip>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      render: (_, record) => (
        <Space>
          <Button icon={<EditOutlined />} onClick={() => handleEdit(record)}>Edit</Button>
          <Popconfirm
            title="Remove this PIN?"
            description="The Pi will reject this PIN immediately on swipe (next swipe attempt)."
            onConfirm={() => handleDelete(record.id)}
            okText="Remove"
            okButtonProps={{ danger: true }}
            cancelText="Cancel"
          >
            <Button icon={<DeleteOutlined />} danger>Remove</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      {contextHolder}
      <Flex justify="space-between" align="center" style={{ marginBottom: 16 }}>
        <span>
          <NumberOutlined style={{ marginRight: 8 }} />
          {rowsVisible.length} active {pluralise(rowsVisible.length, 'PIN')}.
          {' '}Pi polls every ~5 min; deletes + expiry are checked at swipe time, so they revoke immediately.
        </span>
        <Space>
          <Switch
            checked={hideExpired}
            onChange={setHideExpired}
            checkedChildren="Hide expired"
            unCheckedChildren="Show all"
          />
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            Add PIN
          </Button>
        </Space>
      </Flex>

      <Table
        columns={columns}
        dataSource={rowsVisible}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 50 }}
        scroll={{ y: scrollHeight }}
      />

      <Modal
        title={editingPin ? 'Edit PIN' : 'Add PIN'}
        open={modalOpen}
        onOk={handleSubmit}
        onCancel={() => setModalOpen(false)}
        okText="Save"
        destroyOnHidden
        width={560}
      >
        <Form form={form} layout="vertical" preserve={false}>
          <Form.Item
            label="PIN"
            name="pin"
            extra="The digit sequence the user will type on the door keypad before pressing Enter."
            rules={[
              { required: true, message: 'PIN required' },
              { pattern: /^\d{4,12}$/, message: 'PIN should be 4–12 digits' },
            ]}
          >
            <Input placeholder="e.g. 2250" autoComplete="off" />
          </Form.Item>
          <Form.Item
            label="Label"
            name="label"
            extra="What this PIN is for (logged on use). Examples: 'Cleaner Tuesdays', 'Locksmith 2026-05-20', 'Master ATACT'."
            rules={[
              { required: true, message: 'Label required' },
              { min: 1, max: 128, message: 'Label should be 1–128 characters' },
            ]}
          >
            <Input placeholder="e.g. Cleaner Tuesdays" autoComplete="off" />
          </Form.Item>
          <Form.Item
            label="Scope"
            name="doorId"
            extra="Which door this PIN works at. Leave as 'All doors' for site-wide PINs."
          >
            <Select options={doorOptions} />
          </Form.Item>
          <Form.Item
            label="Expires"
            name="expiresAt"
            extra="Optional. After this date/time the PIN stops working. Leave empty for never-expires."
          >
            <DatePicker
              showTime
              format="YYYY-MM-DD HH:mm"
              style={{ width: '100%' }}
              placeholder="Never expires"
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}

SiteConfigDoorKeypadPins.propTypes = {
  siteId: PropTypes.string.isRequired,
};
